import React, { useState, useEffect } from 'react';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import api from '../services/api';

interface SharedCard {
  user_email: string;
  user_name: string;
  card_key: string;
  card_title: string;
  card_config: any;
  filter_conditions?: any[];
  filter_view?: string;
  bucket?: string;
  created_at?: string;
  table_settings?: any;
  /** Home dashboard of the card ('dispatch' | 'lc'). Set by the backend. */
  source_dashboard?: string;
}

interface SharedCardPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (card: SharedCard) => void;
  /** Which dashboard's shared cards to list ('dispatch' | 'lc'). Keeps shares scoped per-dashboard. */
  dashboardType: string;
}

// Map bucket IDs to display names
const BUCKET_NAMES: Record<string, string> = {
  'per-diem-not-dispatched': 'Per Diem Not Dispatched',
  'cutoff-not-dispatched': 'Cutoff Not Dispatched',
  'cutoff-not-terminated': 'Cutoff Not Terminated',
  'drop-released-not-dispatched': 'Drop Released Not Dispatched',
  'terminated-not-complete': 'Terminated Not Complete',
  'no-outgate': 'No Outgate',
  'past-due-no-outgate': 'Past Due No Outgate',
  'past-due': 'Past Due Per Diem',
  'live-load-unload-no-terminate': 'Live Load/Unload, No Terminate',
  'dispatched-driver-oos': 'Dispatched - Driver OOS',
  'dispatches-not-accepted': 'Dispatches Not Accepted',
  'scheduled-not-dispatched': 'Scheduled Not Dispatched',
  'dispatch-started-not-completed': 'Dispatch Started, Not Completed',
  'dispatch-not-started': 'Dispatch Not Started',
  'trace-terminate-no-driver': 'Trace Terminate No Driver',
  'pod-bol-mr-pl-eir-required': 'POD/BOL/MR/PL/EIR Required',
  'invalid-container-numbers': 'Invalid Container Numbers',
  'missing-waiting-time': 'Missing Waiting Time',
  'dispatched-not-yet-arrived': 'Dispatched Not Yet Arrived',
};

// Display names for a card's home dashboard, used to badge cross-dashboard shares.
const DASHBOARD_NAMES: Record<string, string> = {
  dispatch: 'Dispatch',
  lc: 'LC',
};

export default function SharedCardPickerModal({ isOpen, onClose, onSelect, dashboardType }: SharedCardPickerModalProps) {
  const [sharedCards, setSharedCards] = useState<SharedCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSelectedUser(null);
      setUserSearch('');
      api.get(`/settings/shared-cards/${dashboardType}`)
        .then(res => setSharedCards(res.data))
        .catch(err => console.error('Failed to load shared cards:', err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, dashboardType]);

  // Picker: Alt+C closes. Choosing a card is a row click, so there's no chord
  // for it — but the layer keeps Alt+S off the dashboard underneath.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  // Get unique users who have shared cards, sorted alphabetically by name
  // (falling back to email when a name is missing), case-insensitive.
  const users = Array.from(new Map(
    sharedCards.map(c => [c.user_email, { email: c.user_email, name: c.user_name }])
  ).values()).sort((a, b) =>
    (a.name || a.email).localeCompare(b.name || b.email, undefined, { sensitivity: 'base' })
  );

  // Filter the user list by the search box (matches name or email).
  const q = userSearch.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    : users;

  // Cards for selected user, sorted alphabetically by title (case-insensitive).
  const userCards = selectedUser
    ? sharedCards
        .filter(c => c.user_email === selectedUser)
        .sort((a, b) => a.card_title.localeCompare(b.card_title, undefined, { sensitivity: 'base' }))
    : [];

  return (
    <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-lg max-h-[60vh] flex flex-col"
      >
        <ModalHeader
          title={selectedUser ? 'Select a Card' : 'Select a User'}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-sm text-gray-500">Loading shared cards...</span>
            </div>
          ) : !selectedUser ? (
            // Step 1: User list
            <div>
              {users.length > 0 && (
                <div className="sticky top-0 z-10 bg-panel px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <input
                    type="text"
                    autoFocus
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search users..."
                    className="w-full px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No shared cards available
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  No users match &ldquo;{userSearch}&rdquo;
                </div>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.email}
                    onClick={() => setSelectedUser(user.email)}
                    className="w-full px-4 py-3 text-left hover:bg-highlight transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="text-sm font-medium text-heading">{user.name}</div>
                      <div className="text-xs text-muted">{user.email}</div>
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {sharedCards.filter(c => c.user_email === user.email).length} card{sharedCards.filter(c => c.user_email === user.email).length !== 1 ? 's' : ''}
                    </span>
                  </button>
                ))
              )}
              </div>
            </div>
          ) : (
            // Step 2: Card list for selected user
            <div>
              <button
                onClick={() => setSelectedUser(null)}
                className="w-full px-4 py-2 text-left text-xs text-blue-600 dark:text-blue-400 hover:bg-highlight border-b border-gray-200 dark:border-gray-700"
              >
                &larr; Back to users
              </button>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {userCards.map(card => (
                  <button
                    key={card.card_key}
                    onClick={() => {
                      onSelect(card);
                      onClose();
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-highlight transition-colors"
                  >
                    <div className="text-sm font-medium text-heading">{card.card_title}</div>
                    <div className="text-xs text-muted mt-0.5 flex items-center gap-2">
                      <span>{card.created_at ? new Date(card.created_at).toLocaleDateString() : 'Unknown date'}</span>
                      {card.bucket && (
                        <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-[0.625rem] font-medium">
                          Based on: {BUCKET_NAMES[card.bucket] || card.bucket}
                        </span>
                      )}
                      {card.source_dashboard && card.source_dashboard !== dashboardType && (
                        <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-[0.625rem] font-medium">
                          From {DASHBOARD_NAMES[card.source_dashboard] || card.source_dashboard}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
